<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import PageHeader from 'dashboard/components-next/page-header/PageHeader.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import PipelinesAPI from 'dashboard/api/pipelines';
import LeadsAPI from 'dashboard/api/leads';
import LeadContactPanel from '../components/LeadContactPanel.vue';
import LeadNewContactPanel from '../components/LeadNewContactPanel.vue';
import LeadTimeline from '../components/LeadTimeline.vue';
import LeadTasksPanel from '../components/LeadTasksPanel.vue';
import { unwrap, formatLeadCurrency, downloadBlob } from '../helper';
import { DuplicateContactException } from 'shared/helpers/CustomErrors';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { uiSettings } = useUISettings();

// En móvil (< md) las acciones secundarias del header van icon-only (sin label) para
// no recargar la fila. El Button es icon-only nativo cuando label está vacío.
const isMobile = useBreakpoints(breakpointsTailwind).smaller('md');

// Modal "Lead Duplicado": al chocar con un contacto/lead duplicado, muestra el agente
// dueño + desde cuándo (estilo Kommo). Si el lookup no trae dueño (o la setting está
// off) → mensaje genérico. La data la enriquece LeadNewContactPanel en el error.
const duplicateDialog = ref(null);
const duplicateInfo = ref({ agent: null, agentAvatar: '', since: null });
const duplicateAgentName = computed(
  () => duplicateInfo.value.agent || t('LEADS.DUPLICATE.UNASSIGNED')
);
const formattedDuplicateSince = computed(() =>
  duplicateInfo.value.since
    ? format(
        new Date(duplicateInfo.value.since),
        "dd/MM/yyyy 'a las' h:mm aaa",
        {
          locale: es,
        }
      )
    : ''
);
const openDuplicateDialog = data => {
  if (data?.enabled && data?.found) {
    duplicateInfo.value = {
      agent: data.agent,
      agentAvatar: data.agent_avatar || '',
      since: data.since,
    };
    duplicateDialog.value?.open();
  } else {
    useAlert(t('LEADS.FORM.DUPLICATE_CONTACT'));
  }
};

const isLoading = ref(true);
const isSaving = ref(false);
const lead = ref(null);
const stages = ref([]);
const contactPanel = ref(null);
const purgeDialog = ref(null);
// Modo crear (ruta leads_new, sin :leadId): misma página, formulario de
// contacto nativo nuevo (+ cargo, etiquetas, nota) + oportunidad.
const newContactPanel = ref(null);
const createPipelineId = ref(null);

const isCreate = computed(() => !route.params.leadId);
const isArchived = computed(() => !!lead.value?.archived_at);

const form = ref({
  name: '',
  value: '',
  stage_id: null,
  assignee_id: '',
  lost_reason: '',
});

const accountId = computed(() => route.params.accountId);
const leadId = computed(() => route.params.leadId);
const hasContact = computed(() => !!lead.value?.contact_id);

const agents = computed(() => store.getters['agents/getAgents'] || []);
const assigneeOptions = computed(() => [
  { value: '', label: t('LEADS.FORM.UNASSIGNED') },
  ...agents.value.map(agent => ({ value: agent.id, label: agent.name })),
]);
const stageOptions = computed(() =>
  stages.value.map(stage => ({ value: stage.id, label: stage.name }))
);
const selectedStage = computed(() =>
  stages.value.find(stage => stage.id === form.value.stage_id)
);
const isLostStage = computed(() => selectedStage.value?.stage_type === 'lost');

const formattedBudget = computed(() => {
  const amount = Number(form.value.value);
  if (!amount) return null;
  return formatLeadCurrency(amount, lead.value?.currency);
});

const loadLead = async () => {
  isLoading.value = true;
  try {
    const { data } = await LeadsAPI.show(leadId.value);
    lead.value = data?.payload ?? data;
    const { data: stagesData } = await PipelinesAPI.getStages(
      lead.value.pipeline_id
    );
    stages.value = unwrap(stagesData);
    form.value = {
      name: lead.value.name || '',
      value: lead.value.value ?? '',
      stage_id:
        lead.value.stage_id != null ? Number(lead.value.stage_id) : null,
      assignee_id:
        lead.value.assignee_id != null ? Number(lead.value.assignee_id) : '',
      lost_reason: lead.value.lost_reason || '',
    };
  } catch (error) {
    useAlert(t('LEADS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Prepara la página de creación: pipeline (último usado o default) + sus etapas.
const loadForCreate = async () => {
  isLoading.value = true;
  try {
    const { data } = await PipelinesAPI.get();
    const pipelines = unwrap(data);
    const lastId = uiSettings.value?.last_pipeline_id;
    const pipeline =
      pipelines.find(p => p.id === lastId) ||
      pipelines.find(p => p.is_default) ||
      pipelines[0];
    if (!pipeline) {
      useAlert(t('LEADS.LOAD_ERROR'));
      return;
    }
    createPipelineId.value = pipeline.id;
    lead.value = { currency: pipeline.currency || 'USD' };
    const { data: stagesData } = await PipelinesAPI.getStages(pipeline.id);
    stages.value = unwrap(stagesData);
    const incoming =
      stages.value.find(s => s.stage_type === 'incoming') || stages.value[0];
    form.value = {
      name: '',
      value: '',
      stage_id: incoming ? Number(incoming.id) : null,
      assignee_id: '',
      lost_reason: '',
    };
  } catch (error) {
    useAlert(t('LEADS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Crea el Contacto nativo (si se ingresó) y luego el Lead, y va a la ficha.
const create = async () => {
  isSaving.value = true;
  try {
    // Crea el Contacto nativo (base + cargo + etiquetas + nota) y obtiene su id.
    const { contact, partial } =
      (await newContactPanel.value?.createContact()) || {};
    if (!contact?.id) {
      useAlert(t('LEADS.FORM.NAME_REQUIRED'));
      return;
    }
    const { data } = await LeadsAPI.create({
      lead: {
        name: contact.name,
        value: form.value.value === '' ? null : form.value.value,
        stage_id: form.value.stage_id,
        pipeline_id: createPipelineId.value,
        assignee_id:
          form.value.assignee_id === '' ? null : form.value.assignee_id,
        contact_id: contact.id,
        lost_reason: isLostStage.value ? form.value.lost_reason : null,
      },
    });
    const created = data?.payload ?? data;
    // Si una etiqueta/nota no se guardó, avisar (se corrige en la ficha).
    useAlert(
      partial ? t('LEADS.FORM.CREATE_PARTIAL') : t('LEADS.FORM.CREATE_SUCCESS')
    );
    router.replace({
      name: 'leads_show',
      params: { accountId: accountId.value, leadId: created.id },
    });
  } catch (error) {
    // Contacto/lead duplicado → modal con el dueño (LeadNewContactPanel enriquece
    // error.data con { enabled, found, agent, since }). Si no hay dueño → mensaje genérico.
    if (error instanceof DuplicateContactException) {
      openDuplicateDialog(error.data);
    } else {
      useAlert(t('LEADS.FORM.ERROR'));
    }
  } finally {
    isSaving.value = false;
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    const name = hasContact.value
      ? contactPanel.value?.getContactName() || form.value.name
      : form.value.name;
    await LeadsAPI.update(leadId.value, {
      lead: {
        name,
        value: form.value.value === '' ? null : form.value.value,
        stage_id: form.value.stage_id,
        assignee_id:
          form.value.assignee_id === '' ? null : form.value.assignee_id,
        lost_reason: isLostStage.value ? form.value.lost_reason : null,
      },
    });
    if (contactPanel.value) await contactPanel.value.save();
    useAlert(t('LEADS.FORM.SAVE_SUCCESS'));
    loadLead();
  } catch (error) {
    useAlert(t('LEADS.FORM.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

// "Eliminar" = archivar (soft-delete): sale del tablero, se conserva.
const removeLead = async () => {
  try {
    await LeadsAPI.delete(leadId.value);
    useAlert(t('LEADS.ARCHIVE.ARCHIVED_SUCCESS'));
    router.push({
      name: 'leads_dashboard_index',
      params: { accountId: accountId.value },
    });
  } catch (error) {
    useAlert(t('LEADS.FORM.ERROR'));
  }
};

// Restaurar (admin): el lead vuelve al tablero activo.
const restoreLead = async () => {
  try {
    await LeadsAPI.restore(leadId.value);
    useAlert(t('LEADS.ARCHIVE.RESTORED_SUCCESS'));
    loadLead();
  } catch (error) {
    useAlert(t('LEADS.FORM.ERROR'));
  }
};

// Borrado definitivo (admin): elimina el lead y su historial. Irreversible.
const purgeLead = async () => {
  try {
    await LeadsAPI.destroyPermanently(leadId.value);
    purgeDialog.value?.close();
    useAlert(t('LEADS.ARCHIVE.DELETED_SUCCESS'));
    router.push({
      name: 'leads_dashboard_index',
      params: { accountId: accountId.value },
    });
  } catch (error) {
    useAlert(t('LEADS.FORM.ERROR'));
  }
};

const goBack = () =>
  router.push({
    name: 'leads_dashboard_index',
    params: { accountId: accountId.value },
  });

const openConversation = () => {
  if (!lead.value?.conversation) return;
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: lead.value.conversation.display_id,
    },
  });
};

// Carga los inboxes CONTACTABLES del contacto en el store (los pone en
// contact_inboxes) para que ComposeConversation pueda ofrecerlos. La página de lead
// no los precarga (la de contactos sí); sin esto, un contacto sin conversación previa
// muestra "no hay bandejas". Lazy en el click → corre después de cualquier
// contacts/show de montaje, sin race; el watch de ComposeConversation lo recoge.
const prepareContactableInboxes = () => {
  const contactId = lead.value?.contact_id;
  if (contactId) store.dispatch('contacts/fetchContactableInbox', contactId);
};

const isExporting = ref(false);
const exportLead = async () => {
  isExporting.value = true;
  try {
    const { data } = await LeadsAPI.exportLead(leadId.value);
    downloadBlob(data, `lead-${leadId.value}.csv`);
  } catch (error) {
    useAlert(t('LEADS.EXPORT.ERROR'));
  } finally {
    isExporting.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  if (isCreate.value) loadForCreate();
  else loadLead();
});

// Vue reutiliza el componente al navegar (misma ruta, distinto :leadId, o de
// crear → ficha). Recargar la ficha cuando hay un id.
watch(leadId, val => {
  if (val) loadLead();
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <PageHeader show-back @back="goBack">
      <template #title>
        <h1 class="text-lg font-semibold truncate text-n-slate-12">
          {{
            isCreate
              ? t('LEADS.FORM.CREATE_TITLE')
              : form.name || t('LEADS.DETAIL.TITLE')
          }}
        </h1>
        <span
          v-if="isArchived"
          class="inline-flex flex-shrink-0 items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-n-amber-3 text-n-amber-11"
        >
          <span class="i-lucide-archive size-3" />
          {{ t('LEADS.PIPELINE.ARCHIVED_BADGE') }}
        </span>
      </template>
      <template #actions>
        <!-- Iniciar una conversación nueva con el contacto del lead (reusa el flujo
             nativo de contactos: elige inbox + escribe el primer mensaje). -->
        <ComposeConversation
          v-if="!isCreate && lead?.contact_id && !lead?.conversation"
          :contact-id="String(lead.contact_id)"
        >
          <template #trigger>
            <Button
              slate
              faded
              icon="i-lucide-message-circle-plus"
              :label="isMobile ? '' : t('LEADS.DETAIL.NEW_CONVERSATION')"
              @click="prepareContactableInboxes"
            />
          </template>
        </ComposeConversation>
        <Button
          v-if="!isCreate && lead?.conversation"
          slate
          faded
          icon="i-lucide-message-circle"
          :label="isMobile ? '' : t('LEADS.DETAIL.OPEN_CONVERSATION')"
          @click="openConversation"
        />
        <Button
          v-if="!isCreate"
          slate
          faded
          icon="i-lucide-download"
          :label="isMobile ? '' : t('LEADS.EXPORT.LABEL')"
          :is-loading="isExporting"
          @click="exportLead"
        />
        <!-- Crear / guardar. Archivado (admin): restaurar / borrar definitivo. -->
        <Button
          v-if="isCreate"
          :label="t('LEADS.FORM.CREATE')"
          :is-loading="isSaving"
          @click="create"
        />
        <Button
          v-else-if="!isArchived"
          :label="t('LEADS.FORM.SAVE')"
          :is-loading="isSaving"
          @click="save"
        />
        <template v-if="isArchived && isAdmin">
          <Button
            icon="i-lucide-archive-restore"
            :label="t('LEADS.ARCHIVE.RESTORE')"
            @click="restoreLead"
          />
          <Button
            ruby
            faded
            icon="i-lucide-trash-2"
            :label="t('LEADS.ARCHIVE.DELETE_PERMANENTLY')"
            @click="purgeDialog.open()"
          />
        </template>
      </template>
    </PageHeader>

    <div v-if="isLoading" class="flex items-center justify-center flex-1">
      <Spinner />
    </div>

    <div v-else-if="lead" class="flex-1 overflow-y-auto bg-n-alpha-1">
      <div class="grid grid-cols-1 gap-5 p-4 lg:grid-cols-3 max-w-screen-2xl">
        <!-- Columna izquierda: oportunidad + contacto -->
        <div class="flex flex-col gap-5 lg:col-span-2">
          <!-- Oportunidad -->
          <section
            class="flex flex-col gap-4 p-5 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1"
          >
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center min-w-0 gap-2">
                <h3 class="text-base font-semibold text-n-slate-12">
                  {{ t('LEADS.FORM.OPPORTUNITY') }}
                </h3>
                <span
                  v-if="selectedStage"
                  class="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-12"
                >
                  <span
                    class="rounded-full size-2"
                    :style="{
                      backgroundColor: selectedStage.color || '#94A3B8',
                    }"
                  />
                  {{ selectedStage.name }}
                </span>
              </div>
              <span
                v-if="formattedBudget"
                class="text-lg font-semibold text-n-teal-11 whitespace-nowrap"
              >
                {{ formattedBudget }}
              </span>
            </div>

            <Input
              v-if="!hasContact && !isCreate"
              v-model="form.name"
              :label="t('LEADS.FORM.NAME')"
            />

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <!-- Presupuesto -->
              <div class="flex flex-col gap-1">
                <label
                  class="flex items-center gap-2 text-heading-3 text-n-slate-12"
                >
                  <span
                    class="i-lucide-circle-dollar-sign size-4 text-n-slate-11"
                  />
                  {{ t('LEADS.FORM.VALUE') }}
                </label>
                <!-- Input nativo soporta type="number" (handleInput convierte a Number;
                     base class ya incluye [appearance:textfield] para ocultar spinners) -->
                <Input v-model="form.value" type="number" />
              </div>
              <!-- Etapa -->
              <div class="flex flex-col gap-1">
                <label
                  class="flex items-center gap-2 text-heading-3 text-n-slate-12"
                >
                  <span
                    class="i-lucide-git-branch-plus size-4 text-n-slate-11"
                  />
                  {{ t('LEADS.FORM.STAGE') }}
                </label>
                <!-- Select nativo: w-fit por defecto, class="w-full" lo sobreescribe -->
                <Select
                  v-model="form.stage_id"
                  :options="stageOptions"
                  class="w-full"
                />
              </div>
              <!-- Agente -->
              <div class="flex flex-col gap-1">
                <label
                  class="flex items-center gap-2 text-heading-3 text-n-slate-12"
                >
                  <span class="i-lucide-square-user size-4 text-n-slate-11" />
                  {{ t('LEADS.FORM.ASSIGNEE') }}
                </label>
                <Select
                  v-model="form.assignee_id"
                  :options="assigneeOptions"
                  class="w-full"
                />
              </div>
              <Input
                v-if="isLostStage"
                v-model="form.lost_reason"
                class="sm:col-span-3"
                :label="t('LEADS.FORM.LOST_REASON')"
              />
            </div>
          </section>

          <!-- Contacto: nuevo (crear) o existente (editar) -->
          <section
            class="p-5 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1"
          >
            <LeadNewContactPanel v-if="isCreate" ref="newContactPanel" />
            <LeadContactPanel
              v-else-if="hasContact"
              ref="contactPanel"
              :contact-id="lead.contact_id"
              @merged="loadLead"
            />
          </section>

          <!-- Tareas (solo en edición, no en crear) -->
          <section
            v-if="!isCreate"
            class="p-5 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1"
          >
            <LeadTasksPanel
              :lead-id="leadId"
              :assignee-options="assigneeOptions"
            />
          </section>

          <Button
            v-if="!isArchived && !isCreate"
            link
            :label="t('LEADS.ARCHIVE.ARCHIVE')"
            class="self-start text-xs"
            @click="removeLead"
          />
        </div>

        <!-- Columna derecha: historial (solo en edición) -->
        <aside v-if="!isCreate" class="lg:col-span-1">
          <section
            class="p-5 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1 lg:sticky lg:top-5"
          >
            <LeadTimeline :lead-id="leadId" />
          </section>
        </aside>
      </div>
    </div>

    <Dialog
      ref="purgeDialog"
      type="alert"
      :title="t('LEADS.ARCHIVE.DELETE_PERMANENTLY')"
      :description="t('LEADS.ARCHIVE.DELETE_CONFIRM')"
      :confirm-button-label="t('LEADS.ARCHIVE.DELETE_PERMANENTLY')"
      @confirm="purgeLead"
    />

    <!-- Aviso de lead duplicado: agente dueño + desde cuándo (estilo Kommo). -->
    <Dialog
      ref="duplicateDialog"
      type="alert"
      width="sm"
      :title="t('LEADS.DUPLICATE.TITLE')"
      :description="t('LEADS.DUPLICATE.DESCRIPTION')"
      :show-confirm-button="false"
      :cancel-button-label="t('LEADS.DUPLICATE.CLOSE')"
    >
      <div
        class="flex items-center gap-3 p-3 border rounded-xl border-n-strong"
      >
        <Avatar
          :name="duplicateAgentName"
          :src="duplicateInfo.agentAvatar"
          :size="40"
          rounded-full
        />
        <div class="flex flex-col min-w-0 gap-0.5">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ duplicateAgentName }}
          </span>
          <span
            v-if="formattedDuplicateSince"
            class="text-xs truncate text-n-slate-11"
          >
            {{
              t('LEADS.DUPLICATE.SINCE_INLINE', {
                date: formattedDuplicateSince,
              })
            }}
          </span>
        </div>
      </div>
    </Dialog>
  </div>
</template>
